/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
// The only import directly from `zod`. All other components should import this file.
// eslint-disable-next-line no-restricted-imports
import { z } from 'zod';

import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';

import { KubernetesIdentifier, Languages, OAUTH2_PROVIDERS } from '../consts';
import { CountryCodes } from '../countries';
import { OrganizationPermissions, OrganizationRoles, ProjectPermissions, ProjectRoles } from '../rbac';
import { SERVICE_TYPES } from '../service-types';

extendZodWithOpenApi(z);

const languageErrorMap: z.ZodErrorMap = (issue, ctx) => {
  return { message: issue.message!, path: issue.path, received: ctx.data };
};

export { z };

export enum Status {
  Pending = 'Pending',
  Creating = 'Creating',
  Updating = 'Updating',
  Running = 'Running',
  Stopped = 'Stopped',
  Stopping = 'Stopping',
  Starting = 'Starting',
  Invalid = 'Invalid',
  Terminating = 'Terminating',
  ErrorNotFound = 'ErrorNotFound',
  ErrorResourceRetrieve = 'ErrorResourceRetrieve',
  ErrorInvalidState = 'ErrorInvalidState',

  // Custom states
  Importing = 'Importing',
  Unavailable = 'Unavailable',
  Error = 'Error',
}

export enum PgHbaConnectionTypes {
  Host = 'host',
  HostSsl = 'hostssl',
  HostNoSsl = 'hostnossl',
}

export enum PgHbaAuthenticationMethod {
  ScramSha256 = 'scram-sha-256',
  Md5 = 'md5',
  Trust = 'trust',
}

export enum Notifications {
  UiFirstPartyClusterCreated = 'ui_first_party_cluster_created',
  UiFirstPartyClusterUpdated = 'ui_first_party_cluster_updated',
  UiFirstPartyClusterDeleted = 'ui_first_party_cluster_deleted',
  UiFirstPartyClusterUnavailable = 'ui_first_party_cluster_unavailable',
  UiSecondPartyClusterCreated = 'ui_second_party_cluster_created',
  UiSecondPartyClusterUpdated = 'ui_second_party_cluster_updated',
  UiSecondPartyClusterDeleted = 'ui_second_party_cluster_deleted',
  UiSecondPartyClusterUnavailable = 'ui_second_party_cluster_unavailable',
  EmailFirstPartyClusterCreated = 'email_first_party_cluster_created',
  EmailFirstPartyClusterUpdated = 'email_first_party_cluster_updated',
  EmailFirstPartyClusterDeleted = 'email_first_party_cluster_deleted',
  EmailFirstPartyClusterUnavailable = 'email_first_party_cluster_unavailable',
  EmailSecondPartyClusterCreated = 'email_second_party_cluster_created',
  EmailSecondPartyClusterUpdated = 'email_second_party_cluster_updated',
  EmailSecondPartyClusterDeleted = 'email_second_party_cluster_deleted',
  EmailSecondPartyClusterUnavailable = 'email_second_party_cluster_unavailable',
}

const TeamIdentifierRegex = /^[a-z-_]{1,256}$/;
const TeamDisplayNameRegex = /^[a-z-_]([a-z-_]|([a-z-_ ]{1,254}[a-z-_]))?$/i;

const ThumbnailSeedFormat = z.strictObject({
  type: z.literal('seed'),
  seed: z
    .string()
    .min(1)
    .regex(/^[A-Za-z0-9+/]+$/, 'invalid_base64'),
});

const ThumbnailImageFormat = z.strictObject({
  type: z.literal('base64'),
  base64: z
    .string()
    .regex(/^data:image\/(png|jpeg|jpg|gif|webp)(;charset=utf-8)?;base64,[\w\d/+=]+$/, 'invalid_base64_image')
    .min(1),
});

export const ThumbnailFormat = z.discriminatedUnion('type', [ThumbnailSeedFormat, ThumbnailImageFormat]);

export const ZodTypes = {
  Id: z
    .string()
    .regex(/^(0|[1-9][0-9]*)$/, 'invalid_positive_number_string')
    .describe('ID'),
  KubernetesIdentifier: z
    .string()
    .min(KubernetesIdentifier.MinLength)
    .max(KubernetesIdentifier.MaxLength)
    .regex(KubernetesIdentifier.Regex),

  ServiceType: z.enum(SERVICE_TYPES),

  Base64: z.string().regex(/^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/, 'invalid_base64'),

  Language: z.enum(Languages, { errorMap: languageErrorMap, message: 'invalid_enum_value' }),

  NoSpacesString: z.string().trim(),

  IpAndRange: z
    .string()
    .regex(
      /^(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\/(\d|[1-2]\d|3[0-2])$/,
      'invalid_ipv4',
    ),

  Timestamp: z
    .string()
    .regex(
      /^([0-9]+)-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])[Tt]([01][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9]|60)(\.[0-9]+)?(([Zz])|([+-]([01][0-9]|2[0-3]):[0-5][0-9]))$/,
    ),

  SanitizedString: z.string().regex(/^(?=[A-Za-z0-9])([A-Za-z0-9h]*)(?<=[A-Za-z0-9])$/),

  Base64Image: z
    .string()
    .regex(/^data:image\/(png|jpeg|jpg|gif|webp)(;charset=utf-8)?;base64,[\w\d/+=]+$/, 'invalid_base64_image'),

  ThumbnailSeedFormat,
  ThumbnailImageFormat,
  ThumbnailFormat,

  Base64Name: z.string().regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/, 'invalid_base64'),

  TrimmedString: z.string().regex(/^(?!\s).*(?<!\s)$/),
  Password: z
    .string()
    .min(6)
    .regex(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*$/),

  ClusterStatus: z.enum([
    'Pending',
    'Creating',
    'Updating',
    'Running',
    'Stopped',
    'Stopping',
    'Starting',
    'Invalid',
    'Terminating',
    'ErrorNotFound',
    'ErrorResourceRetrieve',
    'ErrorInvalidState',
    'Importing',
    'Unavailable',
    'Error',
  ]),
  ClusterGWAction: z.enum([
    'cluster_created',
    'cluster_updated',
    'cluster_deleted',
    'logical_backup_completed',
    'logical_backup_failed',
    'import_started',
    'import_successful',
    'import_aborted',
    'import_failed_insufficient_space',
    'import_failed_schema_exists',
    'import_failed_database_not_found',
    'import_failed_invalid_credentials',
    'import_failed',
  ]),

  Notification: z.nativeEnum(Notifications),

  Team: {
    Identifier: z.string().regex(TeamIdentifierRegex),
    IdentifierRegex: TeamIdentifierRegex,
    DisplayName: z.string().regex(TeamDisplayNameRegex),
    DisplayNameRegex: TeamDisplayNameRegex,
    About: z.string().trim().min(1).max(1000),
  },

  RBAC: {
    Organization: {
      Roles: z.enum(OrganizationRoles),
      Permissions: z.enum(OrganizationPermissions),
    },
    Project: {
      Roles: z.enum(ProjectRoles),
      Permissions: z.enum(ProjectPermissions),
    },
  },

  UserPassword: z
    .string()
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,}$/, 'invalid_password')
    .min(8),

  NumberString: z.string().regex(/^[0-9]\d*$/, 'invalid_number_string'),

  PgHba: {
    ConnectionTypes: z.nativeEnum(PgHbaConnectionTypes),
    AuthenticationMethod: z.nativeEnum(PgHbaAuthenticationMethod),
  },

  Country: z.enum(CountryCodes),

  SetupStepProgress: z.enum(['pending', 'completed']),
  OAuth2Providers: z.enum(OAUTH2_PROVIDERS),

  DeepthinkerJobStatus: z.enum(['success', 'failed', 'running', 'unknown']),
};
